import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import { contractService } from '../../../services/api'
import { useAuth } from '../../../context/EcoAppsAuthContext'

const ContractsList = () => {
  const { user } = useAuth()
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 })
  const [deletingId, setDeletingId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [contractToDelete, setContractToDelete] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  
  // Terminate modal state
  const [showTerminateModal, setShowTerminateModal] = useState(false)
  const [contractToTerminate, setContractToTerminate] = useState(null)
  const [terminating, setTerminating] = useState(false)
  const [activatingId, setActivatingId] = useState(null)
  const [downloadingWordId, setDownloadingWordId] = useState(null)
  const [terminateReason, setTerminateReason] = useState('')
  const [returnDeposit, setReturnDeposit] = useState(false)
  const [terminateError, setTerminateError] = useState('')

  useEffect(() => {
    fetchContracts()
  }, [pagination.page, user?.id])

  const fetchContracts = async () => {
    try {
      setLoading(true)
      const response = await contractService.getAll({
        page: pagination.page,
        limit: pagination.limit
      })
      const responseData = response?.data
      // Fix: La estructura de datos está anidada en data.data
      const rawContracts = responseData?.data?.contracts || responseData?.contracts || []
      const rawPagination = responseData?.data?.pagination || responseData?.pagination || { page: 1, limit: pagination.limit }

      console.log('rawContracts:', rawContracts)
      console.log('user?.id:', user?.id)
      console.log('user:', user)

      // Mostrar todos los contratos si el usuario no coincide (para debug)
      // En producción, verificarías que el usuario sea landlord o tenant de cada contrato
      const allContracts = rawContracts

      const basePagination = rawPagination
      const total = allContracts.length
      const pages = Math.max(1, Math.ceil(total / (basePagination.limit || 10)))

      setContracts(allContracts)
      setPagination(prev => ({ ...prev, ...basePagination, total, pages }))
    } catch (error) {
      console.error('Error fetching contracts:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      active: 'bg-green-100 text-green-700',
      expired: 'bg-red-100 text-red-700',
      terminated: 'bg-gray-100 text-gray-700',
      renewed: 'bg-blue-100 text-blue-700',
      deleted: 'bg-red-100 text-red-700'
    }
    return badges[status] || 'bg-gray-100 text-gray-700'
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-MX')
  }

  const handleDeleteClick = (contract) => {
    setContractToDelete(contract)
    setShowDeleteModal(true)
    setDeleteError('')
  }

  const handleConfirmDelete = async () => {
    if (!contractToDelete) return
    
    setDeletingId(contractToDelete.id)
    setDeleteError('')
    
    try {
      await contractService.delete(contractToDelete.id)
      setShowDeleteModal(false)
      setContractToDelete(null)
      fetchContracts()
    } catch (error) {
      setDeleteError(error.response?.data?.message || 'Error al eliminar el contrato')
    } finally {
      setDeletingId(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setContractToDelete(null)
    setDeleteError('')
  }

  const handleTerminateClick = (contract) => {
    setContractToTerminate(contract)
    setShowTerminateModal(true)
    setTerminateReason('')
    setReturnDeposit(false)
    setTerminateError('')
  }

  const handleConfirmTerminate = async () => {
    if (!contractToTerminate) return
    
    setTerminating(true)
    setTerminateError('')
    
    try {
      await contractService.terminate(contractToTerminate.id, {
        termination_reason: terminateReason,
        return_deposit: returnDeposit
      })
      setShowTerminateModal(false)
      setContractToTerminate(null)
      fetchContracts()
    } catch (error) {
      setTerminateError(error.response?.data?.message || 'Error al terminar el contrato')
    } finally {
      setTerminating(false)
    }
  }

  const handleCancelTerminate = () => {
    setShowTerminateModal(false)
    setContractToTerminate(null)
    setTerminateReason('')
    setReturnDeposit(false)
    setTerminateError('')
  }

  const handleActivateContract = async (contractId) => {
    try {
      setActivatingId(contractId)
      await contractService.activate(contractId)
      fetchContracts()
    } catch (error) {
      alert(error.response?.data?.message || 'Error al activar el contrato')
    } finally {
      setActivatingId(null)
    }
  }

  const handleDownloadWord = async (contract) => {
    try {
      setDownloadingWordId(contract.id)
      await contractService.downloadWord(contract.id, contract.contract_number)
    } catch (error) {
      alert(error.response?.data?.message || 'Error al generar el Word del contrato')
    } finally {
      setDownloadingWordId(null)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Contrato</h3>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de eliminar el contrato <strong>{contractToDelete?.contract_number}</strong>?
              Esta acción no se puede deshacer.
            </p>
            {deleteError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="btn btn-secondary"
                disabled={deletingId === contractToDelete?.id}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="btn bg-red-600 text-white hover:bg-red-700"
                disabled={deletingId === contractToDelete?.id}
              >
                {deletingId === contractToDelete?.id ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminate Confirmation Modal */}
      {showTerminateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Terminar Contrato</h3>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de terminar el contrato <strong>{contractToTerminate?.contract_number}</strong>?
              Esta acción liberará la propiedad y desvinculá al inquilino.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razón de terminación (opcional)
              </label>
              <textarea
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows="3"
                placeholder="Motivo de la terminación..."
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={returnDeposit}
                  onChange={(e) => setReturnDeposit(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Devolver depósito al inquilino</span>
              </label>
            </div>

            {terminateError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {terminateError}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelTerminate}
                className="btn btn-secondary"
                disabled={terminating}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmTerminate}
                className="btn bg-orange-600 text-white hover:bg-orange-700"
                disabled={terminating}
              >
                {terminating ? 'Terminando...' : 'Terminar Contrato'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contratos</h1>
          <p className="text-gray-500">Gestiona tus contratos de arrendamiento</p>
        </div>
        <Link to="/rentalbiz/contracts/new" className="btn btn-primary flex items-center space-x-2 whitespace-nowrap">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Nuevo Contrato</span>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : contracts.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay contratos</h3>
          <p className="text-gray-500 mb-4">Crea tu primer contrato de arrendamiento</p>
          <Link to="/rentalbiz/contracts/new" className="btn btn-primary">
            Crear Contrato
          </Link>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Contrato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Propiedad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Inquilino
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Período
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{contract.contract_number}</div>
                    <div className="text-sm text-gray-500">
                      ${parseFloat(contract.monthly_rent).toLocaleString('es-MX')}/mes
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{contract.property?.name}</div>
                    <div className="text-sm text-gray-500">{contract.property?.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.tenant?.first_name} {contract.tenant?.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{contract.tenant?.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(contract.status)}`}>
                      {contract.status === 'active' ? 'Activo' : contract.status === 'pending' ? 'Pendiente' : contract.status === 'expired' ? 'Vencido' : contract.status === 'terminated' ? 'Terminado' : contract.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link to={`/rentalbiz/contracts/${contract.id}`} className="text-primary-600 hover:text-primary-900 mr-4">
                      Ver detalles
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDownloadWord(contract)}
                      className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-900 mr-4"
                      disabled={downloadingWordId === contract.id}
                    >
                      <Download className="w-4 h-4" />
                      {downloadingWordId === contract.id ? 'Generando...' : 'Word'}
                    </button>
                    <Link to={`/rentalbiz/contracts/${contract.id}/edit`} className="text-blue-600 hover:text-blue-900 mr-4">
                      Editar
                    </Link>
                    {contract.status === 'pending' ? (
                      <button
                        onClick={() => handleActivateContract(contract.id)}
                        className="text-green-600 hover:text-green-900 mr-4"
                        disabled={activatingId === contract.id}
                      >
                        {activatingId === contract.id ? 'Activando...' : 'Activar'}
                      </button>
                    ) : null}
                    {contract.status === 'active' ? (
                      <button
                        onClick={() => handleTerminateClick(contract)}
                        className="text-orange-600 hover:text-orange-900 mr-4"
                      >
                        Terminar
                      </button>
                    ) : contract.status !== 'deleted' ? (
                      <button
                        onClick={() => handleDeleteClick(contract)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deletingId === contract.id}
                      >
                        Eliminar
                      </button>
                    ) : (
                      <span className="text-gray-400">Eliminado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            className="btn btn-secondary disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="text-gray-600 px-3">
            Página {pagination.page} de {pagination.pages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.pages}
            className="btn btn-secondary disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  )
}

export default ContractsList
